import * as THREE from 'three';
import { CELL, GROUND } from '../core/config';
import { key, MATERIALS, type Building, type City, type Cell, type RoadEdge } from './city';
import type { Vec } from '../destruction/structure';

/** Two traffic lanes leave clearance for opposing full-width coaches. */
export function roadLaneOffset(width: number): number {
  return Math.min(1.65, width * 0.25);
}

export function roadLength(edge: RoadEdge): number {
  return (edge.profile ?? []).reduce(
    (sum, p, i, points) => (i ? sum + Math.hypot(p.x - points[i - 1].x, p.z - points[i - 1].z) : sum),
    0,
  );
}

/** Arc-length sampling, including lane/pavement offsets, shared by both networks. */
export function roadPoint(edge: RoadEdge, t: number, lateral = 0): Vec {
  const profile = edge.profile!;
  let remaining = THREE.MathUtils.clamp(t, 0, 1) * roadLength(edge);
  for (let i = 1; i < profile.length; i++) {
    const a = profile[i - 1],
      b = profile[i],
      length = Math.hypot(b.x - a.x, b.z - a.z);
    if (remaining <= length || i === profile.length - 1) {
      const f = Math.min(1, remaining / Math.max(0.001, length));
      return {
        x: a.x + (b.x - a.x) * f - ((b.z - a.z) / length) * lateral,
        y: a.y + (b.y - a.y) * f,
        z: a.z + (b.z - a.z) * f + ((b.x - a.x) / length) * lateral,
      };
    }
    remaining -= length;
  }
  return { ...profile[0] };
}

export function roadProgress(edge: RoadEdge, point: { x: number; z: number }): number {
  let best = Infinity,
    along = 0,
    result = 0;
  const profile = edge.profile!;
  for (let i = 1; i < profile.length; i++) {
    const a = profile[i - 1],
      b = profile[i],
      dx = b.x - a.x,
      dz = b.z - a.z;
    const length = Math.hypot(dx, dz),
      f = THREE.MathUtils.clamp(((point.x - a.x) * dx + (point.z - a.z) * dz) / (length * length), 0, 1);
    const distance = (point.x - a.x - dx * f) ** 2 + (point.z - a.z - dz * f) ** 2;
    if (distance < best) {
      best = distance;
      result = along + length * f;
    }
    along += length;
  }
  return result / Math.max(0.001, along);
}

function bankCurve(a: Vec, b: Vec, c: Vec, d: Vec): Vec[] {
  const points: Vec[] = [];
  for (let i = 0; i <= 20; i++) {
    const t = i / 20,
      s = 1 - t;
    points.push({
      x: s * s * s * a.x + 3 * s * s * t * b.x + 3 * s * t * t * c.x + t * t * t * d.x,
      y: 0,
      z: s * s * s * a.z + 3 * s * s * t * b.z + 3 * s * t * t * c.z + t * t * t * d.z,
    });
  }
  let length = 0;
  const distances = points.map((p, i) => {
    if (i) length += Math.hypot(p.x - points[i - 1].x, p.z - points[i - 1].z);
    return length;
  });
  points.forEach((p, i) => {
    p.y = a.y + ((d.y - a.y) * distances[i]) / length;
  });
  return points;
}

/** Shared elevation profile: mesh, exact cell collision and route samples use it. */
export function roadHeight(edge: RoadEdge, x: number, z: number): number {
  const profile = edge.profile;
  if (!profile?.length) return GROUND;
  let best = Infinity,
    height = profile[0].y;
  for (let i = 0; i < profile.length - 1; i++) {
    const a = profile[i],
      b = profile[i + 1],
      dx = b.x - a.x,
      dz = b.z - a.z;
    const t = THREE.MathUtils.clamp(((x - a.x) * dx + (z - a.z) * dz) / (dx * dx + dz * dz), 0, 1);
    const d = (x - a.x - dx * t) ** 2 + (z - a.z - dz * t) ** 2;
    if (d < best) {
      best = d;
      height = a.y + (b.y - a.y) * t;
    }
  }
  return height;
}

/** Height of the occupied strip planes at a bend, including the raised kerb.
 * Adjacent rotated strips overlap at their corners; projection onto just the
 * nearest centreline is not the height of their union. */
export function roadSurfaceHeight(edge: RoadEdge, x: number, z: number): number {
  let height = -Infinity;
  const profile = edge.profile ?? [];
  for (let i = 1; i < profile.length; i++) {
    const a = profile[i - 1],
      b = profile[i],
      dx = b.x - a.x,
      dz = b.z - a.z,
      length = Math.hypot(dx, dz);
    const along = ((x - a.x) * dx + (z - a.z) * dz) / length;
    const lateral = ((z - a.z) * dx - (x - a.x) * dz) / length;
    const deck = edge.curved && i === 22;
    if (
      along < -(deck ? 0.015 : jointOverlap(edge, i - 1)) ||
      along > length + (deck ? 0.015 : jointOverlap(edge, i)) ||
      Math.abs(lateral) > (deck ? (Math.ceil((edge.width + 3) / CELL) * CELL) / 2 : edge.width / 2 + 1.5)
    )
      continue;
    const raised = !edge.curved || (i === 22 && edge.bridgeId !== undefined);
    height = Math.max(
      height,
      a.y + ((b.y - a.y) * along) / length + (raised && Math.abs(lateral) > edge.width / 2 ? 0.12 : 0),
    );
  }
  return height === -Infinity ? roadHeight(edge, x, z) : height;
}

function jointOverlap(edge: RoadEdge, index: number): number {
  const p = edge.profile;
  if (!edge.curved || !p || index === 0 || index === p.length - 1) return 0;
  const a = p[index - 1],
    b = p[index],
    c = p[index + 1];
  const ab = Math.atan2(b.z - a.z, b.x - a.x),
    bc = Math.atan2(c.z - b.z, c.x - b.x);
  const turn = Math.acos(Math.cos(ab - bc));
  // The fixed deck is not extended, so its adjoining ramp supplies the whole
  // outside corner. Other joints share the overlap between their two strips.
  return (edge.width / 2 + 1.5) * Math.tan(index === 21 || index === 22 ? turn : turn / 2) + 0.025;
}

export function roadSegment(id: number, edge: RoadEdge, a: Vec, b: Vec, halfWidth: number): Building {
  const length = Math.hypot(b.x - a.x, b.z - a.z),
    slope = Math.atan2(b.y - a.y, length);
  const q = new THREE.Quaternion()
    .setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.atan2(b.z - a.z, b.x - a.x))
    .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), slope));
  const span = Math.hypot(length, b.y - a.y),
    nx = Math.ceil(span / CELL),
    nz = Math.ceil((halfWidth * 2) / CELL);
  const lateral = new THREE.Vector3(0, 0, -halfWidth).applyQuaternion(q);
  const cells: Cell[] = [];
  for (let x = 0; x < nx; x++)
    for (let z = 0; z < nz; z++) {
      const width = Math.min(CELL, span - x * CELL) / CELL,
        depth = Math.min(CELL, halfWidth * 2 - z * CELL) / CELL;
      const side = (z + 0.5) * CELL - halfWidth;
      const pavement = Math.abs(side) > edge.width / 2;
      cells.push({
        id: id * 1000000 + key(x, 0, z),
        x,
        y: 0,
        z,
        hp: MATERIALS.concrete.strength * 1.4,
        material: 'concrete',
        role: 'slab',
        root: Math.abs(slope) < 0.001 || x === 0 || x === nx - 1,
        color: pavement ? 0xc7c8b9 : 0x65726f,
        bounds: {
          x: pavement && !edge.curved ? (0.12 * Math.tan(slope)) / CELL : 0,
          y: -0.2 / CELL,
          z: 0,
          w: width,
          h: (pavement && !edge.curved ? 0.32 : 0.2) / CELL,
          d: depth,
        },
      });
    }
  return {
    id,
    name: `${edge.kind} ${edge.id} · surface`,
    kind: 'road',
    category: 'prop',
    x: a.x + lateral.x,
    z: a.z + lateral.z,
    baseY: a.y + lateral.y,
    rotation: { x: q.x, y: q.y, z: q.z, w: q.w },
    w: nx,
    d: nz,
    h: 1,
    color: 0x65726f,
    accent: 0xe6debe,
    cells,
    roadEdge: edge.id,
    chunk: `road:${edge.id}`,
  };
}

export function upgradeRoads(city: City): City {
  if (!city.roadGraph || city.infrastructure) return city;
  let id =
    Math.max(...[...city.buildings, ...(city.props ?? []), ...(city.bridges ?? [])].map((b) => b.id)) + 1;
  city.infrastructure = [];
  const nodes = new Map(city.roadGraph.nodes.map((n) => [n.id, n]));
  for (const n of nodes.values()) {
    n.y ??= GROUND + 0.2;
    n.layer ??= 'street';
  }
  // The old 82 m crossings left only 13–20 m for a five-metre climb.
  // Reconnect their ramps to the next bank junctions. No road below is raised.
  for (const edge of city.roadGraph.edges) {
    if (edge.bridgeId === undefined) continue;
    const a = nodes.get(edge.from)!,
      b = nodes.get(edge.to)!;
    if (Math.hypot(b.x - a.x, b.z - a.z) >= 125) continue;
    const west = [...nodes.values()].find((n) => n.x === a.x && n.z === a.z - 44);
    const east = [...nodes.values()].find((n) => n.x === b.x && n.z === b.z + 44);
    if (west && east) {
      edge.bridgeZ = a.z;
      edge.curved = true;
      edge.maxVehicleLength = 5.2;
      edge.from = west.id;
      edge.to = east.id;
    }
  }
  const nodeExtent = new Map(
    [...nodes.keys()].map((id) => [
      id,
      Math.max(
        ...city.roadGraph!.edges.filter((e) => e.from === id || e.to === id).map((e) => e.width / 2 + 1.5),
      ),
    ]),
  );
  for (const edge of city.roadGraph.edges) {
    const a = nodes.get(edge.from)!,
      b = nodes.get(edge.to)!;
    edge.layer = edge.bridgeId === undefined ? 'street' : `bridge:${edge.bridgeId}`;
    edge.pavementWidth = 1.5;
    edge.clearance = 4.5;
    edge.surfaceIds = [];
    const profile: Vec[] = [{ x: a.x, y: a.y!, z: a.z }];
    if (edge.bridgeId !== undefined) {
      const bridge = city.bridges!.find((v) => v.id === edge.bridgeId)!;
      const width = Math.ceil((edge.width + 3) / CELL) * CELL;
      const row = edge.bridgeZ ?? a.z;
      bridge.z = row - width / 2;
      bridge.d = width / CELL;
      const cells: Cell[] = [];
      for (let x = 0; x < bridge.w; x++)
        for (let z = 0; z < bridge.d; z++) {
          const pavement = Math.abs((z + 0.5) * CELL - width / 2) > edge.width / 2;
          cells.push({
            id: bridge.id * 1000000 + key(x, 3, z),
            x,
            y: 3,
            z,
            hp: 4.2,
            material: 'metal',
            role: 'beam',
            color: pavement ? 0xc2c7bb : 0x6d7875,
            ...(pavement ? { bounds: { x: 0, y: 0, z: 0, w: 1, h: 1 + 0.12 / CELL, d: 1 } } : {}),
          });
          if (z === 0 || z === bridge.d - 1) {
            cells.push({
              id: bridge.id * 1000000 + key(x, 4, z),
              x,
              y: 4,
              z,
              hp: 4.2,
              material: 'metal',
              role: 'beam',
              color: 0xb1c3bf,
              bounds: { x: 0, y: 0, z: z === 0 ? 0.02 : 0.86, w: 1, h: 0.76, d: 0.12 },
            });
            if (x === 0 || x === Math.floor(bridge.w / 2) || x === bridge.w - 1)
              for (let y = 0; y < 3; y++)
                cells.push({
                  id: bridge.id * 1000000 + key(x, y, z),
                  x,
                  y,
                  z,
                  hp: 4.2,
                  material: 'concrete',
                  role: 'column',
                  color: 0xadb4a7,
                });
          }
        }
      bridge.cells = cells;
      bridge.h = 5;
      const deck = (bridge.baseY ?? GROUND) + 4 * CELL;
      profile.push({ x: bridge.x, y: deck, z: row }, { x: bridge.x + bridge.w * CELL, y: deck, z: row });
    }
    profile.push({ x: b.x, y: b.y!, z: b.z });
    const length = Math.hypot(b.x - a.x, b.z - a.z),
      dx = (b.x - a.x) / length,
      dz = (b.z - a.z) / length;
    if (edge.curved) {
      const left = profile[1],
        right = profile[2];
      const start = { x: a.x + nodeExtent.get(a.id)!, y: a.y!, z: a.z };
      const end = { x: b.x - nodeExtent.get(b.id)!, y: b.y!, z: b.z };
      const west = bankCurve(start, { ...start, x: a.x + 22 }, { ...left, x: left.x - 16 }, left);
      const east = bankCurve(right, { ...right, x: right.x + 16 }, { ...end, x: end.x - 16 }, end);
      profile.splice(0, profile.length, { x: a.x, y: a.y!, z: a.z }, ...west, ...east, {
        x: b.x,
        y: b.y!,
        z: b.z,
      });
    } else {
      profile.splice(1, 0, {
        x: a.x + dx * nodeExtent.get(edge.from)!,
        y: a.y!,
        z: a.z + dz * nodeExtent.get(edge.from)!,
      });
      profile.splice(profile.length - 1, 0, {
        x: b.x - dx * nodeExtent.get(edge.to)!,
        y: b.y!,
        z: b.z - dz * nodeExtent.get(edge.to)!,
      });
    }
    if (
      city.regional &&
      edge.bridgeId === undefined &&
      a.x === b.x &&
      [-510, -420].includes(a.x) &&
      a.z >= 72 &&
      b.z <= 340 &&
      length >= 45
    ) {
      // Keep both public junctions and every existing building identity. These
      // orchard streets bend inside their clear setbacks, using one profile
      // for road geometry, grounded traffic and both pavement routes.
      const start = profile[1],
        end = profile[profile.length - 2],
        bend: Vec[] = [];
      for (let k = 0; k <= 10; k++) {
        const t = k / 10;
        bend.push({
          x: start.x - 4.2 * Math.sin(Math.PI * t) ** 2,
          y: start.y + (end.y - start.y) * t,
          z: start.z + (end.z - start.z) * t,
        });
      }
      profile.splice(1, profile.length - 2, ...bend);
      edge.curved = true;
      edge.kind = 'orchard lane';
    }
    edge.profile = profile;
    edge.maxGrade = 0;
    for (let i = 0; i < profile.length - 1; i++) {
      if (
        i === 0 ||
        i === profile.length - 2 ||
        (edge.bridgeId !== undefined && i === (edge.curved ? 21 : 2))
      )
        continue;
      const start = profile[i],
        end = profile[i + 1],
        length = Math.hypot(end.x - start.x, end.z - start.z);
      edge.maxGrade = Math.max(edge.maxGrade, Math.abs(end.y - start.y) / length);
      // Keep cell coordinates inside the stable-ID grid; junction meshes meet
      // at their boundaries instead of laying crossing road quads on each other.
      const trimStart = -jointOverlap(edge, i),
        trimEnd = -jointOverlap(edge, i + 1);
      const count = Math.ceil((length - trimStart - trimEnd) / 32);
      for (let s = 0; s < count; s++) {
        const t0 = (trimStart + ((length - trimStart - trimEnd) * s) / count) / length,
          t1 = (trimStart + ((length - trimStart - trimEnd) * (s + 1)) / count) / length;
        const p = (t: number) => ({
          x: start.x + (end.x - start.x) * t,
          y: start.y + (end.y - start.y) * t,
          z: start.z + (end.z - start.z) * t,
        });
        const surface = roadSegment(id++, edge, p(t0), p(t1), edge.width / 2 + 1.5);
        city.infrastructure.push(surface);
        edge.surfaceIds.push(surface.id);
      }
    }
  }
  for (const node of nodes.values()) {
    const roads = city.roadGraph.edges.filter((e) => e.from === node.id || e.to === node.id);
    const half = Math.max(...roads.map((e) => e.width / 2 + 1.5));
    const surface = roadSegment(
      id++,
      { ...roads[0], width: half * 2 },
      { x: node.x - half, y: node.y!, z: node.z },
      { x: node.x + half, y: node.y!, z: node.z },
      half,
    );
    surface.name = `Junction ${node.id}`;
    city.infrastructure.push(surface);
  }
  return city;
}
